import { Fragment, useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useLanguage } from '../../i18n/language.js';
import { workspaceCopy } from '../../merchant/copy.js';
import {
  formatRelative,
  lineActionLabel,
  lineStatusLabel,
  type LineStatusKey,
  type LineSummary,
} from '../../merchant/model.js';
import {
  useMerchantWorkspaceIdentity,
  useWorkspace,
} from '../../merchant/shell-context.js';
import {
  Button,
  ButtonLink,
  Callout,
  Card,
  EmptyState,
  PageHead,
  PlusIcon,
  Poster,
  SearchIcon,
  Tag,
  WarningIcon,
} from '../../merchant/ui/primitives.js';
import { NewProductionLineModal } from './NewProductionLineModal.js';

type SortKey = 'recent' | 'name' | 'videos';

const STATUS_ORDER: readonly LineStatusKey[] = [
  'draft',
  'needs-input',
  'analyzing',
  'ready-to-choose',
  'script-approved',
  'generating',
  'complete',
  'failed',
  'active',
  'paused',
  'archived',
];

/**
 * The Production Lines index from the wireframe: search, status filter,
 * sort and count over the six-column row, the failed-row callout, the
 * "no results" state and the first-run empty state. `/production-lines/new`
 * opens the New Production Line modal over the same list.
 */
export function ProductionLinesIndexPage(): ReactNode {
  const { language } = useLanguage();
  const copy = workspaceCopy(language);
  const workspace = useWorkspace();
  const { refresh: refreshWorkspace } = workspace;
  const { businessName } = useMerchantWorkspaceIdentity();
  const navigate = useNavigate();
  const location = useLocation();
  const titleId = useId();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'all' | LineStatusKey>('all');
  const [sort, setSort] = useState<SortKey>('recent');
  const creating = location.pathname === '/production-lines/new';
  const workspaceName = businessName?.trim() || copy.nav.yourBusiness;

  // Returning from a journey must read current durable state, not retain the
  // shell's pre-approval snapshot. This never starts or approves any work.
  useEffect(() => {
    void refreshWorkspace();
  }, [refreshWorkspace, location.pathname]);

  const present = useMemo(
    () => new Set(workspace.lines.map((line) => line.status)),
    [workspace.lines],
  );
  const statusOptions = STATUS_ORDER.filter(
    (key) =>
      present.has(key) ||
      [
        'draft',
        'needs-input',
        'analyzing',
        'ready-to-choose',
        'generating',
        'complete',
        'failed',
      ].includes(key),
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLocaleLowerCase(language);
    const rows = workspace.lines.filter(
      (line) =>
        (status === 'all' || line.status === status) &&
        (query.length === 0 ||
          `${line.title} ${line.prompt} ${line.offer} ${line.domain}`
            .toLocaleLowerCase(language)
            .includes(query)),
    );
    return [...rows].sort((a, b) =>
      sort === 'name'
        ? a.title.localeCompare(b.title, language)
        : sort === 'videos'
          ? b.videos - a.videos
          : new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
  }, [language, search, sort, status, workspace.lines]);

  const openNew = () => {
    void navigate('/production-lines/new');
  };
  const closeNew = () => {
    void navigate('/production-lines', { replace: true });
  };

  const newButton = (
    <Button
      variant="primary"
      onClick={openNew}
      disabled={workspace.creationDestination === null && !workspace.loading}
      title={
        workspace.creationDestination === null && !workspace.loading
          ? copy.lines.createUnavailable
          : undefined
      }
    >
      <PlusIcon />
      {copy.lines.newLine}
    </Button>
  );

  return (
    <section
      className="mj-ws-page"
      aria-labelledby={titleId}
      data-screen-label="Production Lines"
    >
      <PageHead
        eyebrow={workspaceName}
        title={copy.lines.title}
        titleId={titleId}
        intro={copy.lines.intro}
        action={newButton}
      />

      {workspace.failed ? (
        <Callout className="mj-ws-mt-24" role="alert">
          <WarningIcon />
          <span className="mj-ws-callout-copy mj-ws-sm">
            <strong>{copy.common.unavailableTitle}</strong>{' '}
            {copy.common.unavailableBody}
          </span>
          <Button small onClick={() => void workspace.refresh()}>
            {copy.common.tryAgain}
          </Button>
        </Callout>
      ) : null}

      {workspace.loading ? (
        <p className="mj-ws-loading" role="status">
          {copy.common.loading}
        </p>
      ) : workspace.lines.length === 0 ? (
        <EmptyState
          roomy
          title={<span className="mj-ws-h2">{copy.lines.startTitle}</span>}
          body={copy.lines.startBody}
          action={
            <Button variant="primary" onClick={openNew}>
              {copy.lines.newLine}
            </Button>
          }
        >
          <Poster width={72} state="playable" />
        </EmptyState>
      ) : (
        <>
          <div role="search" className="mj-ws-toolbar">
            <label className="mj-ws-srch">
              <span className="mj-ws-vh">{copy.lines.search}</span>
              <SearchIcon />
              <input
                className="mj-ws-inp"
                type="search"
                placeholder={copy.lines.searchPlaceholder}
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                }}
              />
            </label>
            <label>
              <span className="mj-ws-vh">{copy.lines.filter}</span>
              <select
                className="mj-ws-inp"
                value={status}
                onChange={(event) => {
                  setStatus(event.target.value as 'all' | LineStatusKey);
                }}
              >
                <option value="all">{copy.lines.allStatuses}</option>
                {statusOptions.map((key) => (
                  <option key={key} value={key}>
                    {lineStatusLabel(key, copy)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span className="mj-ws-vh">{copy.lines.sort}</span>
              <select
                className="mj-ws-inp"
                value={sort}
                onChange={(event) => {
                  setSort(event.target.value as SortKey);
                }}
              >
                <option value="recent">{copy.lines.sortRecent}</option>
                <option value="name">{copy.lines.sortName}</option>
                <option value="videos">{copy.lines.sortVideos}</option>
              </select>
            </label>
            <span
              className="mj-ws-muted mj-ws-sm mj-ws-tnum mj-ws-toolbar-count"
              aria-live="polite"
            >
              {copy.common.linesCount(filtered.length)}
            </span>
          </div>

          {filtered.length > 0 ? (
            <Card padding={0}>
              <div className="mj-ws-line-table-wrap">
                <table className="mj-ws-line-table" aria-label={copy.lines.title}>
                  <thead>
                    <tr>
                      <th scope="col">{copy.lines.colLine}</th>
                      <th scope="col">{copy.lines.colStatus}</th>
                      <th scope="col">
                        <span className="mj-ws-vh">{copy.action.open}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((line) => (
                      <LineRow key={line.id} line={line} />
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ) : (
            <EmptyState
              body={copy.lines.noResults}
              action={
                <Button
                  small
                  onClick={() => {
                    setSearch('');
                    setStatus('all');
                  }}
                >
                  {copy.lines.clear}
                </Button>
              }
            />
          )}
        </>
      )}

      {creating ? (
        <NewProductionLineModal
          onClose={closeNew}
          onCreated={(intakeId, lineId) => {
            void navigate(
              lineId === null
                ? `/business-profile/business?intake=${encodeURIComponent(intakeId)}`
                : `/production-lines/${encodeURIComponent(lineId)}`,
              { replace: true },
            );
          }}
        />
      ) : null}
    </section>
  );
}

function LineRow({ line }: { readonly line: LineSummary }): ReactNode {
  const { language } = useLanguage();
  const copy = workspaceCopy(language);
  return (
    <Fragment>
      <tr>
        <th scope="row">
          <Link to={line.href} className="mj-ws-lrow-title">
            <bdi>{line.title}</bdi>
          </Link>
          {line.offer ? <div className="mj-ws-muted mj-ws-xs">{line.offer}</div> : null}
          <div className="mj-ws-muted mj-ws-sm mj-ws-lrow-excerpt" dir="auto">
            {line.prompt ? `“${line.prompt}”` : copy.common.noBriefYet}
          </div>
          <div className="mj-ws-muted mj-ws-xs mj-ws-mt-8">
            <bdi>{line.domain || copy.common.dash}</bdi>
            {' · '}
            {copy.common.videosCount(line.videos)}
          </div>
        </th>
        <td>
          <Tag status={line.status}>{lineStatusLabel(line.status, copy)}</Tag>
          <div className="mj-ws-muted mj-ws-xs mj-ws-mt-8">
            {formatRelative(line.updatedAt, copy, language)}
          </div>
        </td>
        <td className="mj-ws-lrow-end">
          <ButtonLink to={line.actionHref} small>
            {lineActionLabel(line.action, copy)}
          </ButtonLink>
        </td>
      </tr>
      {line.status === 'failed' ? (
        <tr className="mj-ws-line-table-error">
          <td colSpan={3}>
            <Callout>
              <WarningIcon />
              <span className="mj-ws-callout-copy mj-ws-sm">
                <strong>{copy.lines.couldNotCreate}</strong> {line.failReason ?? ''}
              </span>
              <ButtonLink to={line.href} variant="quiet" small>
                {copy.action.issue}
              </ButtonLink>
            </Callout>
          </td>
        </tr>
      ) : null}
    </Fragment>
  );
}
